import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  Calendar,
  Clock,
  Video,
  Eye,
  RefreshCcw,
} from 'lucide-react'
import { interviewerApi } from '../../lib/api/interviewer'

interface Session {
  id: number
  title: string
  client_name: string
  scheduled_at: string
  duration_minutes: number
  status: string
  is_rescheduled?: boolean
  recording_url?: string
  has_notes?: boolean
}

export default function InterviewerSessions() {
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<Session[]>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchSessions()
  }, [])

  const fetchSessions = async () => {
    setLoading(true)
    try {
      const res = await interviewerApi.getSessions()
      setSessions(res.data || res || [])
    } catch (err) {
      console.error('Failed to fetch sessions:', err)
    } finally {
      setLoading(false)
    }
  }

  const filteredSessions = sessions.filter((session) => {
    const clientName = session.client_name || ''
    const matchesSearch = clientName.toLowerCase().includes(search.toLowerCase())
    const matchesStatus =
      statusFilter === 'all' || session.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return 'badge-success'
      case 'scheduled':
        return 'badge-primary'
      case 'pending_review':
        return 'badge-warning'
      case 'cancelled':
        return 'badge-danger'
      default:
        return 'badge-primary'
    }
  }

  const formatStatus = (status: string) =>
    status
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })

  if (loading) {
    return <div className="loading-screen">Loading sessions...</div>
  }

  return (
    <div>
      <div
        className="page-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <h1 className="page-title">Session Management</h1>
          <p className="page-subtitle">View and manage all interview sessions</p>
        </div>
        <button className="btn btn-secondary" onClick={fetchSessions}>
          <RefreshCcw size={18} />
          Refresh
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="icon primary">
            <Calendar size={24} />
          </div>
          <div className="value">
            {sessions.filter((s) => s.status === 'scheduled').length}
          </div>
          <div className="label">Upcoming</div>
        </div>
        <div className="stat-card">
          <div className="icon success">
            <Video size={24} />
          </div>
          <div className="value">
            {sessions.filter((s) => s.status === 'completed').length}
          </div>
          <div className="label">Completed</div>
        </div>
        <div className="stat-card">
          <div className="icon warning">
            <Clock size={24} />
          </div>
          <div className="value">
            {sessions.filter((s) => s.status === 'pending_review').length}
          </div>
          <div className="label">Pending Review</div>
        </div>
      </div>

      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flex: 1,
              minWidth: 200,
            }}
          >
            <Search size={18} style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              className="form-input"
              placeholder="Search by client name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ border: 'none', boxShadow: 'none' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['all', 'scheduled', 'completed', 'pending_review', 'cancelled'].map(
              (status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  style={{
                    padding: '6px 14px',
                    border: '1px solid var(--border)',
                    background:
                      statusFilter === status ? 'var(--primary-navy)' : 'white',
                    color:
                      statusFilter === status ? 'white' : 'var(--text-muted)',
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {status === 'all' ? 'All' : formatStatus(status)}
                </button>
              )
            )}
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Date & Time</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSessions.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: 'center',
                      padding: 40,
                      color: 'var(--text-muted)',
                    }}
                  >
                    No sessions found
                  </td>
                </tr>
              ) : (
                filteredSessions.map((session) => (
                  <tr key={session.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="avatar">
                          {(session.client_name || 'C').charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {session.client_name || 'Unknown Client'}
                          </div>
                          <div
                            style={{ fontSize: 12, color: 'var(--text-muted)' }}
                          >
                            {session.title}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}
                      >
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 13,
                          }}
                        >
                          <Calendar size={14} />
                          {formatDate(session.scheduled_at)}
                        </span>
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 13,
                            color: 'var(--text-muted)',
                          }}
                        >
                          <Clock size={14} />
                          {formatTime(session.scheduled_at)}
                        </span>
                      </div>
                    </td>
                    <td>{session.duration_minutes || 60} min</td>
                    <td>
                      <span className={`badge ${getStatusBadge(session.status)}`}>
                        {session.is_rescheduled && (
                          <RefreshCcw
                            size={12}
                            style={{ marginRight: 4 }}
                          />
                        )}
                        {formatStatus(session.status)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: 13 }}
                        onClick={() =>
                          navigate(`/interviewer/sessions/${session.id}`)
                        }
                      >
                        <Eye size={14} />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
